/** Producer-exclusive: Title Registry. */
import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { Film, Plus, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function ProducerFeaturePanel({ org, quorum, accent = "#F5B82E" }) {
  const [titles, setTitles] = useState([]);
  const [total, setTotal]   = useState(0);
  const [open, setOpen]     = useState(false);
  const unlocked = quorum?.full_features_unlocked;

  const refresh = async () => {
    if (!unlocked) return;
    try {
      const { data } = await api.get(`/orgs/${org.id}/title-registry`, { params: { limit: 20 } });
      setTitles(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.warn("[ProducerFeaturePanel] title registry load skipped:", err?.message);
    }
  };
  useEffect(() => { void refresh(); }, [unlocked, org.id]);

  return (
    <div className="cs-card rounded-2xl p-6" data-testid="producer-title-registry-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center"
               style={{ background: `${accent}20`, color: accent }}>
            <Film size={16} />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/55">Title Registry</div>
            <div className="font-display text-xl uppercase tracking-tight" data-testid="titles-total">
              {total.toLocaleString()} titles registered
            </div>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={!unlocked}
                    className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em]"
                    style={unlocked ? { background: accent, color: "#0b0b0d" } : {}}
                    data-testid="register-title-btn">
              <Plus size={12} className="mr-1.5" /> Register Title
            </Button>
          </DialogTrigger>
          <RegisterTitleDialog orgId={org.id} onDone={() => { setOpen(false); refresh(); }} />
        </Dialog>
      </div>
      {!unlocked && (
        <div className="mt-4 flex items-center gap-2 text-[11px] text-amber-300">
          <ShieldAlert size={12} /> Feature unlocks after quorum + KYC.
        </div>
      )}
      {unlocked && titles.length === 0 && (
        <div className="mt-4 text-sm text-white/55">No titles yet. Register your first title above.</div>
      )}
      {unlocked && titles.length > 0 && (
        <ul className="mt-4 divide-y divide-white/5" data-testid="titles-list">
          {titles.map((t) => (
            <li key={t.id} className="py-2.5 flex items-center gap-3" data-testid={`title-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{t.title}</div>
                <div className="text-[11px] text-white/55">
                  {t.registrant_name} · {t.project_type} · {new Date(t.registered_at).toLocaleDateString()}
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] tracking-[0.18em] uppercase ${
                t.status === "disputed" ? "bg-red-500/15 text-red-300"
                                        : "bg-emerald-500/10 text-emerald-300"
              }`}>{t.status}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RegisterTitleDialog({ orgId, onDone }) {
  const [title, setTitle] = useState("");
  const [registrant, setRegistrant] = useState("");
  const [logline, setLogline] = useState("");
  const [projectType, setProjectType] = useState("film");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orgs/${orgId}/title-registry`, {
        title, registrant_name: registrant, logline, project_type: projectType,
      });
      if (data.duplicate_warning) {
        toast.warning(`Duplicate flagged — matches "${data.duplicate_warning.registrant_name}" from ${new Date(data.duplicate_warning.registered_at).toLocaleDateString()}`);
      } else {
        toast.success("Title registered");
      }
      onDone?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not register");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="bg-[#0c0c0e] border-white/10 max-w-md" data-testid="register-title-dialog">
      <DialogHeader>
        <DialogTitle className="font-display uppercase tracking-tight">Register a Title</DialogTitle>
        <DialogDescription className="text-white/55 text-xs">
          Duplicate detection is soft — a warning surfaces but the row is still stored.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Title *</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
                 className="mt-2 h-10 bg-black/40 border-white/15" data-testid="title-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Registrant *</Label>
            <Input value={registrant} onChange={(e) => setRegistrant(e.target.value)}
                   className="mt-2 h-10 bg-black/40 border-white/15" data-testid="registrant-input" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Project Type</Label>
            <select value={projectType} onChange={(e) => setProjectType(e.target.value)}
                    className="mt-2 w-full h-10 rounded-md bg-black/40 border border-white/15 px-3 text-white text-sm"
                    data-testid="project-type-select">
              {["film","ad_film","tv","web_series","format_show","screenplay","story","format","other"].map(p => (
                <option key={p} value={p}>{p.replace("_"," ")}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">Logline</Label>
          <Input value={logline} onChange={(e) => setLogline(e.target.value)}
                 className="mt-2 h-10 bg-black/40 border-white/15" data-testid="logline-input" />
        </div>
        <Button onClick={submit} disabled={!title || !registrant || busy}
                className="w-full btn-themed h-11 rounded-full text-xs uppercase tracking-[0.25em]"
                data-testid="submit-title-btn">
          {busy ? "Registering…" : "Register Title"}
        </Button>
      </div>
    </DialogContent>
  );
}
